import { Card, Table, TableBody, TableContainer } from '@mui/material';
import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  PositionTxType,
  PositionFilters,
  PositionFilterKey,
  PositionFilterValue,
} from 'src/type/position';
import { isAfter } from 'src/utils/format-time';
import Scrollbar from 'src/components/scrollbar';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { useBoolean } from 'src/hooks/use-boolean';
import { TransactionType, TransactionStatus, TransactionBadgeType } from 'src/type/transaction';
import usePagination from 'src/hooks/use-pagination';
import { fParams, fDateRange, ParamsType } from 'src/utils/format-params';
import axios, { endpoints } from 'src/utils/axios';
import { Pagination } from 'src/type/utils';
import { toastError } from 'src/utils/format-error';
import TableLoading from 'src/components/table/table-loading';
import { emptyRows } from 'src/components/table/utils';
import { UserType } from 'src/type/user';
import StatusTabFilter, { StatusTabFilterOption } from 'src/components/ui-kit/status-tab-filter';
import { BranchType } from 'src/type/branch';
import PositionTableToolbar from './position-table-toolbar';
import PositionTableFiltersResult from './position-table-filters-result';
import PositionTableRow from './position-table-row';
import PositionCreateDialog from './position-create-dialog';

const defaultFilters: PositionFilters = {
  name: null,
  type: 'all',
  startDate: null,
  endDate: null,
  status: 'all',
};

const tableHead = [
  { id: '', width: 60 },
  { id: 'ลูกค้า', label: 'ลูกค้า' },
  { id: 'product', label: 'สินค้า', width: 140 },
  { id: 'sent', label: 'น้ำหนัก', width: 120 },
  { id: 'createDate', label: 'วันที่ทำรายการ', width: 180 },
  // { id: 'dueDate', label: 'วันที่อนุมัติ', width: 160 },
  { id: 'status', label: 'สถานะ', width: 120 },
  { id: 'action', width: 60 },
];

type Props = {
  user?: UserType;
  rootReload?: () => void;
};

export default function PositionTableView({ user, rootReload }: Props) {
  const initialize = useBoolean(true);
  const [filters, setFilters] = useState(defaultFilters);
  const form = useBoolean(false);
  const reload = useBoolean(false);

  const [branches, setBranches] = useState<BranchType[]>([]);

  const [badge, setBadge] = useState<TransactionBadgeType>({
    total: 0,
    waiting: 0,
    waitingPayment: 0,
    waitingTicket: 0,
    pending: 0,
    success: 0,
    rejected: 0,
    cancel: 0,
    failed: 0,
  });

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<PositionTxType>();

  const dateError = isAfter(filters.startDate?.toDate(), filters.endDate?.toDate());

  const canReset =
    !!filters.name ||
    filters.type !== 'all' ||
    filters.status !== 'all' ||
    (!!filters.startDate && !!filters.endDate);

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      ...fDateRange(filters.startDate, filters.endDate),

      type:
        filters.type === 'all'
          ? `${TransactionType.BUY_POSITION},${TransactionType.SELL_POSITION}`
          : filters.type,
      status: filters.status === 'all' ? undefined : filters.status,
      page: currentPage,
      limit: rowsPerPage,
      userId: user?.uid || filters.name?.value,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    handleGetTx(params);
    handleGetBranches();
  }, [
    currentPage,
    rowsPerPage,
    filters.startDate,
    filters.endDate,
    filters.status,
    filters.type,
    reload.value,
    filters.name,
  ]);

  const handleGetBranches = useCallback(async () => {
    try {
      // get branch
      const { data } = await axios.get<Pagination<BranchType>>(endpoints.branch.get, {
        params: {
          page: 1,
          limit: 999,
        },
      });
      setBranches(data.results);
    } catch (error) {
      toastError(error);
    }
  }, []);

  const handleGetTx = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();

      const { data: _badge } = await axios.get<TransactionBadgeType>(
        endpoints.transactions.badges,
        {
          params: {
            type: `${TransactionType.BUY_POSITION},${TransactionType.SELL_POSITION}`,
          },
        }
      );
      setBadge(_badge);

      const { data } = await axios.get<Pagination<PositionTxType>>(endpoints.transactions.get, {
        params: fParams(params),
      });

      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  const handleFilters = useCallback((name: PositionFilterKey, value: PositionFilterValue) => {
    onResetPage();
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleResetFilters = useCallback(() => {
    onResetPage();
    setFilters(defaultFilters);
  }, []);

  const handleReload = useCallback(() => {
    onResetPage();
    if (rootReload) {
      rootReload();
    }
    reload.onToggle();
  }, []);

  const tabs: StatusTabFilterOption = useMemo(
    () => [
      { value: 'all', label: 'ทั้งหมด', color: 'default', count: badge.total },
      {
        value: TransactionStatus.WAITING,
        label: 'รอราคา',
        color: 'info',
        count: badge.waiting,
      },
      {
        value: TransactionStatus.WAITING_PAYMENT,
        label: 'รอชำระเงิน',
        color: 'default',
        count: badge.waitingPayment || 0,
      },
      {
        value: TransactionStatus.WAITING_TICKET,
        label: 'รอดำเนินการ',
        color: 'secondary',
        count: badge.waitingTicket || 0,
      },
      {
        value: TransactionStatus.PENDING,
        label: 'รออนุมัติ',
        color: 'warning',
        count: badge.pending,
      },
      {
        value: TransactionStatus.SUCCESS,
        label: 'สำเร็จ',
        color: 'success',
        count: badge.success,
      },
      {
        value: TransactionStatus.REJECTED,
        label: 'ปฏิเสธ',
        color: 'error',
        count: badge.rejected,
      },
      {
        value: TransactionStatus.CANCEL,
        label: 'ยกเลิก',
        color: 'error',
        count: badge.cancel,
      },
      {
        value: TransactionStatus.FAILED,
        label: 'ไม่สำเร็จ',
        color: 'error',
        count: badge.failed,
      },
    ],
    [badge]
  );

  return (
    <>
      <Card>
        {!user && (
          <StatusTabFilter
            value={filters.status}
            onChange={(newValue) => handleFilters('status', newValue)}
            options={tabs}
          />
        )}
        <PositionTableToolbar
          filters={filters}
          onFilters={handleFilters}
          dateError={dateError}
          disabledSearch={!!user}
          enableCreate={!!user}
          onCreatePosition={form.onTrue}
        />

        {canReset && (
          <PositionTableFiltersResult
            filters={filters}
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            results={meta.totalResults}
            sx={{ p: 2.5, pt: 0 }}
          />
        )}

        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <Scrollbar>
            <Table sx={{ minWidth: 800 }}>
              <TableHeadCustom headLabel={tableHead} />

              <TableBody>
                <TableLoading loading={initialize.value} rows={rowsPerPage}>
                  {tableData.map((row) => (
                    <PositionTableRow
                      key={row.id}
                      row={row}
                      handleReload={handleReload}
                      branches={branches}
                    />
                  ))}

                  <TableEmptyRows
                    show={!initialize.value}
                    height={76}
                    emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                  />

                  <TableNoData notFound={!tableData.length} />
                </TableLoading>
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={meta.totalResults}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Card>
      {!!form.value && !!user && (
        <PositionCreateDialog
          open={form.value}
          onClose={form.onFalse}
          user={user}
          handleReload={handleReload}
        />
      )}
    </>
  );
}
